import React, { useMemo } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import dayjs from 'dayjs';
import classNames from 'classnames';
import ErrorBoundary from '@/app/ErrorBoundary';
import LeafNav from '@/business-components/leaf-nav';
import MenuNav from '@/business-components/menu-nav';
import AccountSelect from '@/components/AccountSelect';
import Breadcrumb, { BreadcrumbProps } from '@/business-components/breadcrumb';
import globalStore from '@/stores';

import './assets/less/index.scoped.less';

export interface BasePageProps {
  /* 当前pathname，可不传 */
  pathname?: string,
  /* 组件类名 */
  className?: string,
  /* 样式 */
  style?: Record<string, any>,
  /* 卡片布局组件，第一个卡片有三级导航 */
  cardComponentList?: React.ReactNode[],
  /* 主体区域组件 */
  bodyComponent?: React.ReactNode,
  /* 二级导航选项 */
  tabNavOptions?: {
    right?: React.ReactNode,
  },
  /* 账户选择选项 */
  accountSelectOptions?: any,
  /* 启用面包屑 */
  useBreadcrumb?: boolean,
  /* 面包屑选项 */
  breadcrumbOptions?: BreadcrumbProps,
  /** 展示侧边导航 */
  showSideNav?: boolean,
  /* 侧边导航选项 */
  menuNavOptions?: any,
  /** 不展示背景 */
  noBackground?: boolean;
}

const Maintainance = () => {
  return <div className="maintainance-page">系统升级中，请稍后访问</div>;
};

const BasePageComponent: React.FC<BasePageProps> = (props) => {
  const location = useLocation();
  const {
    className,
    style = {},
    cardComponentList = [],
    bodyComponent,
    tabNavOptions = {},
    menuNavOptions = {},
    accountSelectOptions,
    useBreadcrumb,
    breadcrumbOptions,
    pathname,
    showSideNav,
    noBackground,
  } = props;

  const tabNavRight = (
    <React.Fragment>
      {/* 附加功能 */}
      {tabNavOptions.right ? tabNavOptions.right : null}
      {/* 分隔线 */}
      {tabNavOptions.right && accountSelectOptions ? <i className="line-v" /> : null}
      {/* 服务商选择 */}
      {accountSelectOptions ? <AccountSelect {...accountSelectOptions} /> : null}
    </React.Fragment>
  );

  const displaySideNav = showSideNav && !useBreadcrumb;

  const isMaintain = useMemo(() => {
    const pageNotice = Object.keys(globalStore.pageNoticeConfig).find((key) => matchPath(location.pathname, key));
    const { maintain } = globalStore.pageNoticeConfig[pageNotice || ''] || {};
    return maintain === true || (maintain && dayjs().format('YYYY-MM-DD') >= maintain);
  }, [location.pathname, globalStore.pageNoticeConfig]);

  return (
    /* 基础页面 */
    <div className={classNames('base-page', className)} style={style}>
      <div className="base-page-header">
        {/* 面包屑 */}
        {useBreadcrumb && <Breadcrumb {...breadcrumbOptions} right={tabNavRight} />}
      </div>
      <div className={classNames('base-page-body', { 'side-nav': displaySideNav })}>
        {/* 错误提示 */}
        <ErrorBoundary>
          {/* 侧边导航 */}
          {displaySideNav && <MenuNav href={pathname} {...menuNavOptions} />}
          {/* 主体区域 */}
          <div className={classNames({ 'base-page-container': displaySideNav })}>
            {/* 末级导航 */}
            <LeafNav href={pathname} endOfHeadContent={tabNavRight} />
            {cardComponentList.filter((v) => v).map((v, i) => {
              return (
                <div className="base-card" key={`card-${i}`}>
                  {v}
                </div>
              );
            })}
            <div className={classNames('base-body-container', { 'no-bg': noBackground })}>
              {isMaintain ? <Maintainance /> : bodyComponent}
            </div>
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
};

BasePageComponent.defaultProps = {
  pathname: '',
  cardComponentList: [],
  bodyComponent: null,
  tabNavOptions: {},
  useBreadcrumb: false,
  breadcrumbOptions: {},
  showSideNav: true,
  menuNavOptions: {},
  noBackground: false,
};

export const BasePage = Object.assign(BasePageComponent, {
  getAgencyUid: (v: any = {}) => AccountSelect.getAgencyUid(v),
  getAgency: (v: any = {}) => AccountSelect.getAgency(v),
  getAgencyList: (v: any = {}) => AccountSelect.getAgencyList(v),
});

export default BasePage;
